/**
 * Iter 125 · Bloque "Extras de facturación" en el detalle de una petición.
 *
 *  Muestra:
 *   1. Componentes marcados como "facturar sin montaje" (production íntegro,
 *      sin cobro de montaje). Botón "💶 Facturar sin montaje" en cada línea
 *      lo activa/desactiva con motivo obligatorio.
 *   2. Conceptos sueltos añadidos al albarán FD de la petición (portes,
 *      desplazamiento perdido, etc.).
 *   3. Botón "Enviar a FacturaDirecta" que genera el albarán con todos los
 *      pendientes en 1 solo click.
 */
import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Receipt, Trash2, Plus, Send, AlertCircle } from "lucide-react";

export default function ExtrasFacturacionBlock({ peticionId, componentes, onChange }) {
  const [extras, setExtras] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showNuevoExtra, setShowNuevoExtra] = useState(false);
  const [nuevoExtra, setNuevoExtra] = useState({
    concepto: "", cantidad: 1, precio_unitario: 0, motivo_interno: "",
  });
  const [saving, setSaving] = useState(false);
  const [marcandoCid, setMarcandoCid] = useState(null);
  const [motivoMarcado, setMotivoMarcado] = useState("");
  const [enviandoFD, setEnviandoFD] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get(`/peticiones/${peticionId}/albaran-extras`);
      setExtras(r.data || []);
    } catch (_) { /* ok */ }
    setLoading(false);
  };
  useEffect(() => { if (peticionId) load(); /* eslint-disable-next-line */ }, [peticionId]);

  const comps = componentes || [];
  const marcados = comps.filter((c) => c?.facturar_sin_montaje?.activo && !c.facturar_sin_montaje.albaran_local_id);
  const yaFacturados = comps.filter((c) => c?.facturar_sin_montaje?.albaran_local_id);

  const importeMarcados = marcados.reduce(
    (s, c) => s + (Number(c.precio_unitario || 0) * Number(c.cantidad || 1)), 0);
  const importeExtras = extras.filter((e) => !e.enviado_fd)
    .reduce((s, e) => s + Number(e.precio_unitario || 0) * Number(e.cantidad || 1), 0);
  const totalPendiente = importeMarcados + importeExtras;

  const marcarComponente = async () => {
    if (!motivoMarcado.trim()) return toast.error("Motivo obligatorio");
    try {
      await api.post(`/componentes/${marcandoCid}/facturar-sin-montaje`, { motivo: motivoMarcado });
      toast.success("Componente marcado como facturable sin montaje");
      setMarcandoCid(null); setMotivoMarcado("");
      onChange && await onChange();
    } catch (err) { toast.error(err?.response?.data?.detail || "Error"); }
  };

  const desmarcarComponente = async (cid) => {
    if (!window.confirm("¿Quitar la marca de facturable sin montaje?")) return;
    try {
      await api.delete(`/componentes/${cid}/facturar-sin-montaje`);
      toast.success("Marca eliminada");
      onChange && await onChange();
    } catch (err) { toast.error(err?.response?.data?.detail || "Error"); }
  };

  const crearExtra = async () => {
    if (!nuevoExtra.concepto.trim() || Number(nuevoExtra.precio_unitario) <= 0) {
      return toast.error("Concepto y precio obligatorios");
    }
    setSaving(true);
    try {
      await api.post(`/peticiones/${peticionId}/albaran-extras`, {
        concepto: nuevoExtra.concepto.trim(),
        cantidad: Number(nuevoExtra.cantidad) || 1,
        precio_unitario: Number(nuevoExtra.precio_unitario),
        motivo_interno: nuevoExtra.motivo_interno.trim() || null,
      });
      toast.success("Concepto añadido");
      setShowNuevoExtra(false);
      setNuevoExtra({ concepto: "", cantidad: 1, precio_unitario: 0, motivo_interno: "" });
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Error"); }
    setSaving(false);
  };

  const borrarExtra = async (eid) => {
    if (!window.confirm("¿Borrar este concepto?")) return;
    try {
      await api.delete(`/peticiones/albaran-extras/${eid}`);
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Error"); }
  };

  const enviarFD = async () => {
    if (marcados.length === 0 && extras.filter((e) => !e.enviado_fd).length === 0) {
      return toast.error("No hay nada pendiente de facturar");
    }
    if (!window.confirm(`Se generará un albarán FD con ${totalPendiente.toFixed(2)}€ (subtotal). ¿Continuar?`)) return;
    setEnviandoFD(true);
    try {
      const r = await api.post(`/peticiones/${peticionId}/facturar-extras-fd`, { nuevo: false });
      toast.success(`Enviado a FD · albarán ${r.data.albaran_numero}`);
      load();
      onChange && await onChange();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Error enviando a FD");
    }
    setEnviandoFD(false);
  };

  return (
    <div className="bg-zinc-900/40 border border-zinc-800 rounded-lg p-4 space-y-3">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Receipt className="w-5 h-5 text-fuchsia-400" />
          <h3 className="text-sm font-bold">Extras facturación</h3>
          <span className="text-[10px] text-zinc-500">(sin montaje / conceptos sueltos)</span>
        </div>
        {totalPendiente > 0 && (
          <div className="text-xs bg-fuchsia-500/15 text-fuchsia-200 border border-fuchsia-500/30 rounded px-2 py-0.5"
               data-testid="extras-total-pendiente">
            Pendiente: <b>{totalPendiente.toFixed(2)}€</b>
          </div>
        )}
      </div>

      {/* Componentes marcados como sin montaje */}
      {comps.length > 0 && (
        <div className="border border-zinc-800 rounded-md p-2 bg-zinc-950/40">
          <div className="text-[11px] uppercase text-zinc-500 mb-1.5">💶 Componentes facturables SIN MONTAJE</div>
          <div className="space-y-1">
            {comps.filter((c) => !c.parent_id).map((c) => {
              const fsm = c.facturar_sin_montaje || {};
              const activo = fsm.activo;
              const facturado = !!fsm.albaran_local_id;
              return (
                <div key={c.id}
                     className={`flex items-center gap-2 text-xs rounded p-1.5 ${
                       facturado ? "bg-emerald-500/5 border border-emerald-500/20" :
                       activo ? "bg-fuchsia-500/10 border border-fuchsia-500/30" :
                       "bg-zinc-900 border border-zinc-800"
                     }`}
                     data-testid={`fsm-row-${c.id}`}>
                  <span className="flex-1 truncate">
                    <b>{c.nombre}</b>
                    {c.ancho_cm && c.alto_cm && <span className="text-zinc-500"> · {c.ancho_cm}×{c.alto_cm}cm</span>}
                    <span className="text-zinc-500"> · {Number(c.precio_unitario || 0).toFixed(2)}€</span>
                  </span>
                  {facturado ? (
                    <span className="text-[10px] text-emerald-300 shrink-0">
                      ✓ facturado {fsm.albaran_numero || ""}
                    </span>
                  ) : activo ? (
                    <>
                      <span className="text-[10px] text-fuchsia-300 italic max-w-[200px] truncate shrink-0" title={fsm.motivo}>
                        {fsm.motivo}
                      </span>
                      <Button size="sm" variant="ghost" className="h-6 w-6 p-0"
                              onClick={() => desmarcarComponente(c.id)}
                              data-testid={`fsm-desmarcar-${c.id}`}>
                        <Trash2 className="w-3 h-3 text-red-400" />
                      </Button>
                    </>
                  ) : (
                    <Button size="sm" variant="outline" className="h-6 text-[10px] px-2 border-fuchsia-500/40 text-fuchsia-200 hover:bg-fuchsia-500/10"
                            onClick={() => { setMarcandoCid(c.id); setMotivoMarcado(""); }}
                            data-testid={`fsm-marcar-${c.id}`}>
                      💶 Facturar sin montaje
                    </Button>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Conceptos sueltos */}
      <div className="border border-zinc-800 rounded-md p-2 bg-zinc-950/40">
        <div className="flex items-center justify-between mb-1.5">
          <div className="text-[11px] uppercase text-zinc-500">➕ Conceptos sueltos al albarán</div>
          <Button size="sm" variant="outline" className="h-6 text-[10px] px-2"
                  onClick={() => setShowNuevoExtra(true)}
                  data-testid="extras-nuevo-btn">
            <Plus className="w-3 h-3 mr-1" /> Añadir
          </Button>
        </div>
        {loading ? (
          <div className="text-[11px] text-zinc-500">Cargando…</div>
        ) : extras.length === 0 ? (
          <div className="text-[11px] text-zinc-500 italic">Ninguno.</div>
        ) : (
          <div className="space-y-1">
            {extras.map((e) => (
              <div key={e.id}
                   className={`flex items-center gap-2 text-xs rounded p-1.5 ${
                     e.enviado_fd ? "bg-emerald-500/5 border border-emerald-500/20" : "bg-zinc-900 border border-zinc-800"
                   }`}
                   data-testid={`extra-row-${e.id}`}>
                <span className="flex-1 truncate">
                  <b>{e.concepto}</b> · {e.cantidad}× {Number(e.precio_unitario).toFixed(2)}€ = <b>{(e.cantidad * e.precio_unitario).toFixed(2)}€</b>
                  {e.motivo_interno && <span className="text-zinc-500 italic ml-1">· {e.motivo_interno}</span>}
                </span>
                {e.enviado_fd ? (
                  <span className="text-[10px] text-emerald-300 shrink-0">✓ facturado</span>
                ) : (
                  <Button size="sm" variant="ghost" className="h-6 w-6 p-0" onClick={() => borrarExtra(e.id)}
                          data-testid={`extra-borrar-${e.id}`}>
                    <Trash2 className="w-3 h-3 text-red-400" />
                  </Button>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {totalPendiente > 0 && (
        <Button onClick={enviarFD} disabled={enviandoFD}
                className="w-full bg-fuchsia-600 hover:bg-fuchsia-700 text-white"
                data-testid="extras-enviar-fd-btn">
          <Send className="w-4 h-4 mr-1" />
          {enviandoFD ? "Enviando a FacturaDirecta…" : `Enviar ${totalPendiente.toFixed(2)}€ a FacturaDirecta`}
        </Button>
      )}

      {yaFacturados.length > 0 && (
        <div className="text-[10px] text-zinc-500">
          {yaFacturados.length} componente(s) ya facturados sin montaje.
        </div>
      )}

      {/* Modal marcar componente */}
      <Dialog open={!!marcandoCid} onOpenChange={(o) => { if (!o) setMarcandoCid(null); }}>
        <DialogContent className="bg-zinc-950 border-fuchsia-500/40" data-testid="dialog-marcar-fsm">
          <DialogHeader>
            <DialogTitle className="text-fuchsia-200 flex items-center gap-2">
              <AlertCircle className="w-5 h-5" /> Facturar sin montaje
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-2">
            <p className="text-sm text-zinc-400">
              Se incluirá el <b>precio íntegro de producción</b> en el albarán FD sin cobrar montaje.
              Indica el motivo para tenerlo trazado.
            </p>
            <Label className="text-xs">Motivo (obligatorio)</Label>
            <Textarea
              value={motivoMarcado}
              onChange={(e) => setMotivoMarcado(e.target.value)}
              placeholder="Ej: el cliente canceló el montaje pero se produjo la pieza."
              rows={3}
              className="bg-zinc-900 border-zinc-800"
              data-testid="fsm-motivo-input"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setMarcandoCid(null); setMotivoMarcado(""); }}>Cancelar</Button>
            <Button onClick={marcarComponente} className="bg-fuchsia-600 hover:bg-fuchsia-700"
                    data-testid="fsm-confirmar-btn">
              Marcar como facturable
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal nuevo concepto suelto */}
      <Dialog open={showNuevoExtra} onOpenChange={setShowNuevoExtra}>
        <DialogContent className="bg-zinc-950 border-fuchsia-500/40" data-testid="dialog-nuevo-extra">
          <DialogHeader>
            <DialogTitle className="text-fuchsia-200">Nuevo concepto al albarán</DialogTitle>
          </DialogHeader>
          <div className="space-y-2">
            <div>
              <Label className="text-xs">Concepto *</Label>
              <Input value={nuevoExtra.concepto}
                     onChange={(e) => setNuevoExtra({ ...nuevoExtra, concepto: e.target.value })}
                     placeholder="Ej: Desplazamiento perdido zona norte"
                     className="bg-zinc-900 border-zinc-800"
                     data-testid="extra-concepto"
                     maxLength={200} />
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label className="text-xs">Cantidad</Label>
                <Input type="number" step="0.5" min="0" value={nuevoExtra.cantidad}
                       onChange={(e) => setNuevoExtra({ ...nuevoExtra, cantidad: e.target.value })}
                       className="bg-zinc-900 border-zinc-800"
                       data-testid="extra-cantidad" />
              </div>
              <div>
                <Label className="text-xs">Precio unitario €*</Label>
                <Input type="number" step="0.01" min="0" value={nuevoExtra.precio_unitario}
                       onChange={(e) => setNuevoExtra({ ...nuevoExtra, precio_unitario: e.target.value })}
                       className="bg-zinc-900 border-zinc-800"
                       data-testid="extra-precio" />
              </div>
            </div>
            <div>
              <Label className="text-xs">Motivo interno (opcional)</Label>
              <Textarea rows={2} value={nuevoExtra.motivo_interno}
                        onChange={(e) => setNuevoExtra({ ...nuevoExtra, motivo_interno: e.target.value })}
                        placeholder="Nota interna que no viaja al cliente…"
                        className="bg-zinc-900 border-zinc-800"
                        data-testid="extra-motivo" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowNuevoExtra(false)}>Cancelar</Button>
            <Button onClick={crearExtra} disabled={saving}
                    className="bg-fuchsia-600 hover:bg-fuchsia-700"
                    data-testid="extra-guardar-btn">
              Añadir concepto
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
